import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { fetchCoupons, applyUpgradeCode } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Ticket, ArrowUpRight, Check, AlertCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// CRM "Coupon" action: lists the dealer's coupons; clicking one applies it to
// the selected contact(s) and UPGRADES their role to the coupon's tier
// (upward only — never admin/distributor). Backend: /auth/apply-upgrade-code.
export default function CouponUpgradeModal({ open, onOpenChange, recipients = [], onApplied }) {
  const { user } = useAuth();
  const userId = String(user?.user_id || user?.id || "");

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyingCode, setApplyingCode] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setApplyingCode("");
    setLoading(true);
    fetchCoupons(userId)
      .then((c) => setCoupons(c.coupons || []))
      .catch(() => toast.error("Could not load coupons"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const single = recipients.length === 1 ? recipients[0] : null;

  async function applyCoupon(code) {
    if (!code || !recipients.length) return;
    const picked = coupons.find((c) => c.coupon_name === code);
    const tierLabel = picked?.coupon_tier || "the coupon tier";
    setApplyingCode(code);
    setResults([]);
    const out = [];
    for (const r of recipients) {
      const name = r.full_name || r.first_name || "Contact";
      // Contacts with no linked account can't be role-upgraded — skip the call.
      if (r.has_account === false) {
        out.push({ name, no_account: true });
        continue;
      }
      try {
        const res = await applyUpgradeCode(r.id, code);
        out.push({ name, tier: tierLabel, ...res });
      } catch (e) {
        out.push({ name, error: e?.response?.data?.detail || "Failed" });
      }
    }
    setResults(out);
    setApplyingCode("");
    const upgraded = out.filter((o) => o.upgraded).length;
    const noChange = out.filter((o) => !o.upgraded && !o.error && !o.no_account).length;
    const errored = out.filter((o) => o.error || o.no_account).length;
    if (upgraded) {
      toast.success(`Upgraded ${upgraded} contact${upgraded === 1 ? "" : "s"} to ${tierLabel}`);
    } else if (errored && !noChange) {
      toast.error("Couldn't apply — see details below");
    } else {
      // Nothing was upgraded because everyone is already at/above this tier.
      toast.warning(`No changes — already at or above ${tierLabel}`, {
        description: "Coupons only upgrade a role upward, never sideways or down.",
      });
    }
    onApplied?.();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="coupon-upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket size={18} className="text-violet-600" /> Apply coupon &amp; upgrade role
          </DialogTitle>
          <DialogDescription className="text-xs">
            {single
              ? `Tap a coupon to upgrade ${single.full_name || "this contact"}${single.role_name ? ` (currently ${single.role_name})` : ""} to the coupon's tier.`
              : `Tap a coupon to upgrade the ${recipients.length} selected contacts to the coupon's tier.`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 max-h-72 overflow-y-auto" data-testid="coupon-upgrade-list">
              {coupons.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-zinc-400">No coupons found</div>
              )}
              {coupons.map((c) => {
                const busy = applyingCode === c.coupon_name;
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={!!applyingCode}
                    onClick={() => applyCoupon(c.coupon_name)}
                    className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-violet-50/60 transition-colors disabled:opacity-50"
                    data-testid={`coupon-upgrade-item-${c.coupon_name}`}
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-100 text-violet-600 shrink-0">
                      <Ticket size={16} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{c.coupon_name}</div>
                      {c.coupon_tier && (
                        <div className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1">
                          <ArrowUpRight size={12} /> upgrades to {c.coupon_tier}
                        </div>
                      )}
                    </div>
                    {busy ? (
                      <Loader2 className="animate-spin text-violet-600 shrink-0" size={16} />
                    ) : (
                      <ChevronRight size={16} className="text-zinc-300 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            {results.length > 0 && (
              <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100" data-testid="coupon-upgrade-results">
                {results.map((r, i) => (
                  <div key={i} className="px-3 py-2 text-sm" data-testid={`coupon-upgrade-result-${i}`}>
                    {r.no_account ? (
                      <div className="flex items-center gap-2 text-amber-600">
                        <AlertCircle size={14} /> {r.name}: no linked account — can't upgrade
                      </div>
                    ) : r.error ? (
                      <div className="flex items-center gap-2 text-rose-600">
                        <AlertCircle size={14} /> {r.name}: {r.error}
                      </div>
                    ) : r.upgraded ? (
                      <div className="flex items-center gap-2 text-emerald-700 font-medium">
                        <Check size={14} /> {r.name}: {r.previous_role || "customer"} → <b className="capitalize">{r.new_role}</b>
                        {r.cart_repriced > 0 && <span className="text-[11px] font-normal text-emerald-600" data-testid="coupon-cart-repriced">· cart re-priced ({r.cart_repriced} item{r.cart_repriced === 1 ? "" : "s"})</span>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-zinc-500">
                        <ArrowUpRight size={14} className="text-zinc-300" /> {r.name}: no change — already <span className="capitalize">{r.role || "at or above this tier"}</span>
                        {r.cart_repriced > 0 && <span className="text-[11px] text-emerald-600" data-testid="coupon-cart-repriced">· cart re-priced ({r.cart_repriced})</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
